import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { CourseEnrolment } from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { PORTAL, SEARCH } from 'src/ReduxStorage/resourceNames';
import {
  PortalResource,
  PortalResourceAddEnrolmentsToUserGroups,
  PortalResourceGetAllCourses,
  PortalResourceGetCourseSummaries,
  PortalResourceGetUserGroups,
  PortalResourceRemoveEnrolments,
  PortalResourceUpdateRoleForUsers,
  PortalResourceResendInvitationEmails,
} from 'src/resource/PortalResource/types';
import { ResourceApi } from 'src/resource/Resource';
import {
  SearchResource,
  SearchResourceSuggestUser,
  SearchResourceSuggestUserReturnResult,
} from 'src/resource/SearchResource/types';
import { axiosInstance } from 'src/web/utilities/axios';

export const useSuggestUserApi = (
  initialSearchQuery = '',
  institutionPath: string
): [
  null | SearchResourceSuggestUserReturnResult[],
  (searchQuery: string) => void,
  () => void
] => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [queryResult, setQueryResult] = useState<
    null | SearchResourceSuggestUserReturnResult[]
  >(null);

  const debouncedSuggestUserApiCall = useCallback(
    debounce((searchQuery: string, startIndex: number) => {
      // Do the exact api calll
      const params = {
        types: 'user' as const,
        query: searchQuery,
        institutionPaths: [institutionPath],
        start: startIndex,
      };
      return ResourceApi.lookup<SearchResource, SearchResourceSuggestUser>(
        SEARCH,
        'suggestUser',
        params
      ).then(({ results }) => {
        setQueryResult(results);
      });
    }, 300),
    []
  );

  const loadMore = useCallback((): void => {
    debouncedSuggestUserApiCall(searchQuery, (queryResult?.length ?? 0) + 1);
  }, [searchQuery, queryResult]);

  useEffect(() => {
    // Do the Api call everytime search query change
    if (searchQuery !== '') {
      debouncedSuggestUserApiCall(searchQuery, 0);
    } else {
      setQueryResult(null);
    }
  }, [searchQuery]);

  return [queryResult, setSearchQuery, loadMore];
};

export const useGetAllCoursesApi = (
  institutionPath: string
): PortalResourceGetAllCourses['return'] | null => {
  const [fetchResult, setFetchResult] = useState<
    PortalResourceGetAllCourses['return'] | null
  >(null);

  useEffect(() => {
    ResourceApi.read<PortalResource, PortalResourceGetAllCourses>(
      PORTAL,
      institutionPath,
      'getAllCourses',
      {}
    ).then((result) => setFetchResult(result));
  }, [institutionPath]);

  return fetchResult;
};

export const useGetCoursesInformation = (
  institutionPath: string,
  coursePaths: string[],
  pageSize = 100
): [CourseEnrolment[], boolean, () => void] => {
  const [pageIndex, setPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [coursesData, setCoursesData] = useState<CourseEnrolment[]>([]);

  useEffect(() => {
    if (pageSize > 0) {
      const startLookupCoursePathsIndex = pageIndex * pageSize;
      setIsLoading(true);
      ResourceApi.read<PortalResource, PortalResourceGetCourseSummaries>(
        PORTAL,
        institutionPath,
        'getCourseSummaries',
        {
          coursePaths: coursePaths.slice(
            startLookupCoursePathsIndex,
            startLookupCoursePathsIndex + pageSize
          ),
        }
      ).then((res) => {
        setCoursesData((prev) => [...prev, ...Object.values(res)]);
        setIsLoading(false);
      });
    }
  }, [pageIndex, pageSize]);

  const loadMore = useCallback(() => setPageIndex((prev) => prev + 1), [
    setPageIndex,
  ]);

  return [coursesData, isLoading, loadMore];
};

export const useGetPortalGroupsApi = (
  institutionPath: string
): [PortalResourceGetUserGroups['return']] => {
  const [data, setData] = useState<string[]>([]);
  useEffect(() => {
    ResourceApi.read<PortalResource, PortalResourceGetUserGroups>(
      PORTAL,
      institutionPath,
      'getUserGroups',
      {}
    ).then((res) => {
      setData(res);
    });
  }, [institutionPath]);
  return [data];
};

export const useAddEnrolmentsToUserGroupsApi = (
  institutionPath: string
): {
  addEnrolmentsToUserGroups: (params: {
    userGroups: string[];
    enrolmentIds: string[];
  }) => Promise<unknown>;
} => {
  const addEnrolmentsToUserGroups = useCallback(
    ({
      userGroups,
      enrolmentIds,
    }: {
      userGroups: string[];
      enrolmentIds: string[];
    }) => {
      return ResourceApi.read<
        PortalResource,
        PortalResourceAddEnrolmentsToUserGroups
      >(PORTAL, institutionPath, 'addEnrolmentsToUserGroups', {
        userGroups,
        enrolmentIds,
      });
    },
    [institutionPath]
  );

  return { addEnrolmentsToUserGroups };
};

export const useRemoveEnrolmentsApi = (
  institutionPath: string
): {
  removeEnrolments: (params: { enrolmentIds: string[] }) => Promise<unknown>;
} => {
  const removeEnrolments = useCallback(
    ({ enrolmentIds }: { enrolmentIds: string[] }) => {
      return ResourceApi.update<PortalResource, PortalResourceRemoveEnrolments>(
        PORTAL,
        institutionPath,
        'removeEnrolments',
        {
          enrolmentIds,
        }
      );
    },
    [institutionPath]
  );

  return { removeEnrolments };
};

export const useUpdateRoleForUsersApi = (
  institutionPath: string
): {
  updateRoleForUsers: (params: {
    enrolmentIds: string[];
    role: 'staff' | 'admin';
  }) => Promise<unknown>;
} => {
  const updateRoleForUsers = useCallback(
    ({
      enrolmentIds,
      role,
    }: {
      enrolmentIds: string[];
      role: 'staff' | 'admin';
    }) => {
      return ResourceApi.update<
        PortalResource,
        PortalResourceUpdateRoleForUsers
      >(PORTAL, institutionPath, 'updateRoleForUsers', {
        enrolmentIds,
        role,
      });
    },
    [institutionPath]
  );
  return { updateRoleForUsers };
};

export const useResendInvitationEmailsApi = (
  institutionPath: string
): {
  resendInvitationEmails: (params: { ids: string[] }) => Promise<unknown>;
} => {
  const resendInvitationEmails = useCallback(
    ({ ids }: { ids: string[] }) => {
      return ResourceApi.update<
        PortalResource,
        PortalResourceResendInvitationEmails
      >(PORTAL, institutionPath, 'resendInvitationEmails', {
        invitedUserIds: ids,
      });
    },
    [institutionPath]
  );

  return { resendInvitationEmails };
};

export const useUninvitePendingTeamMembersApi = (
  institutionPath: string
): {
  uninvitePendingTeamMembers: (params: { ids: string[] }) => Promise<unknown>;
} => {
  const uninvitePendingTeamMembers = useCallback(
    ({ ids }: { ids: string[] }) => {
      const formData = new FormData();
      formData.append('email', ids.join(','));
      return axiosInstance.post(
        `/api/institution/uninvite/?institution=${institutionPath}`,
        formData
      );
    },
    [institutionPath]
  );
  return { uninvitePendingTeamMembers };
};
